import { useQuery } from '@apollo/client';
import { GET_CLASSROOM_MEETING_ATTENDANCE } from '../pages/LiveClass/api/LiveClassQuery';
import { MeetingAttendanceResponse } from '../utils/interface';
import { FETCH_POLICY } from '../utils/constants';

interface UseMeetingAttendanceParams {
  classroomId?: string | null;
  subjectId?: string | null;
  sessionId?: string | null;
  /** 1-based page */
  page: number;
  pageSize: number;
  search: string;
}

// The API matches with SQL LIKE, so the typed text is wrapped as %text%; empty text means no filter
export const toSearchPattern = (text: string) => {
  const trimmed = text.trim();
  return trimmed ? `%${trimmed}%` : null;
};

/**
 * Loads one page of a meeting's attendance (summary + students) from the server.
 * While the next page or search result loads, the previous result is kept so the table doesn't flash empty.
 */
export const useMeetingAttendance = ({ classroomId, subjectId, sessionId, page, pageSize, search }: UseMeetingAttendanceParams) => {
  const { data, previousData, loading, error } = useQuery<MeetingAttendanceResponse>(GET_CLASSROOM_MEETING_ATTENDANCE, {
    variables: {
      cId: classroomId,
      sjId: subjectId,
      sessionId,
      first: pageSize,
      offset: (page - 1) * pageSize,
      search: toSearchPattern(search),
    },
    skip: !classroomId || !subjectId || !sessionId,
    fetchPolicy: FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });

  const attendance = (data ?? previousData)?.classroomMeetingAttendance ?? null;
  return { attendance, loading, error };
};
